import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import * as api from '../services/api';
import Header from '../components/Header';
import './Insights.css';

function Insights() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hoveredMonth, setHoveredMonth] = useState(null);
  const { token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    api.getAiInsights()
      .then((data) => { setInsights(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  return (
    <div className="dashboard">
      <Header />

      <div className="dashboard-nav">
        <div className="nav-tabs">
          <button
            className={`nav-tab ${location.pathname === '/' ? 'active' : ''}`}
            onClick={() => navigate('/')}
          >
            {t('overview')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/orders' ? 'active' : ''}`}
            onClick={() => navigate('/orders')}
          >
            {t('orders')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/calendar' ? 'active' : ''}`}
            onClick={() => navigate('/calendar')}
          >
            {t('calendar')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/insights' ? 'active' : ''}`}
            onClick={() => navigate('/insights')}
          >
            {t('insights')}
          </button>
          <button
            className={`nav-tab ${location.pathname === '/administration' ? 'active' : ''}`}
            onClick={() => navigate('/administration')}
          >
            {t('administration')}
          </button>
        </div>
      </div>

      <div className="insights-page-content">
        {loading ? (
          <p className="loading-text">{t('loading')}</p>
        ) : !insights ? (
          <p className="loading-text">{t('noDataAvailable')}</p>
        ) : (
          <>
            <div className="insights-grid">
              <div className="insights-kpi-card">
                <div className="kpi-label">{t('bookings30days')}</div>
                <div className="kpi-value">{insights.bookings_count}</div>
                {insights.trend_percent !== null && (
                  <div className={`kpi-trend ${insights.trend_percent >= 0 ? 'up' : 'down'}`}>
                    {insights.trend_percent >= 0 ? '+' : ''}{insights.trend_percent}% {t('vsPreviousPeriod')}
                  </div>
                )}
              </div>

              <div className="insights-kpi-card">
                <div className="kpi-label">{t('previousPeriod')}</div>
                <div className="kpi-value">{insights.previous_period_count}</div>
                <div className="kpi-sub">{t('last30days')}</div>
              </div>

              <div className="insights-kpi-card">
                <div className="kpi-label">{t('totalUnits')} ({t('last30days')})</div>
                <div className="kpi-value">{insights.total_pallets}</div>
              </div>
            </div>

            {/* Monthly pallets chart */}
            {insights.monthly_pallets && insights.monthly_pallets.length > 0 && (() => {
              const allUnits = new Set();
              insights.monthly_pallets.forEach(m => Object.keys(m.units).forEach(u => allUnits.add(u)));
              const unitList = [...allUnits];
              const unitColors = ['#007566', '#00A896', '#FFB74D', '#7B1FA2', '#1565C0', '#C62828'];
              const maxTotal = Math.max(...insights.monthly_pallets.map(m => Object.values(m.units).reduce((a, b) => a + b, 0)), 1);

              return (
                <div className="insights-chart-card">
                  <h3 className="panel-title">Enheder per måned (12 mdr)</h3>
                  <div className="chart-legend">
                    {unitList.map((u, i) => (
                      <span key={u} className="legend-item">
                        <span className="legend-dot" style={{ background: unitColors[i % unitColors.length] }} />
                        {u}
                      </span>
                    ))}
                  </div>
                  <div className="monthly-chart">
                    {insights.monthly_pallets.map((m, mi) => {
                      const total = Object.values(m.units).reduce((a, b) => a + b, 0);
                      return (
                        <div
                          key={mi}
                          className={`monthly-bar-col ${hoveredMonth === mi ? 'hovered' : ''}`}
                          onMouseEnter={() => setHoveredMonth(mi)}
                          onMouseLeave={() => setHoveredMonth(null)}
                        >
                          {hoveredMonth === mi && total > 0 && (
                            <div className="monthly-tooltip">
                              <strong>{m.month}</strong> — {m.bookings} booking{m.bookings !== 1 ? 'er' : ''}
                              {Object.entries(m.units).map(([unit, qty]) => (
                                <div key={unit} className="tooltip-line">
                                  <span className="tooltip-dot" style={{ background: unitColors[unitList.indexOf(unit) % unitColors.length] }} />
                                  {qty} {unit}
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="monthly-bar-stack" style={{ height: `${(total / maxTotal) * 100}%` }}>
                            {unitList.map((u, ui) => {
                              const val = m.units[u] || 0;
                              if (val === 0) return null;
                              return (
                                <div
                                  key={u}
                                  className="monthly-bar-segment"
                                  style={{ flex: val, background: unitColors[ui % unitColors.length] }}
                                />
                              );
                            })}
                          </div>
                          <div className="monthly-bar-total">{total || ''}</div>
                          <div className="monthly-bar-label">{m.month}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            <div className="insights-panels">
              <div className="insights-panel">
                <h3 className="panel-title">{t('topRoutes')}</h3>
                {insights.top_routes && insights.top_routes.length > 0 ? (
                  <table className="insights-table">
                    <thead>
                      <tr>
                        <th>{t('from')}</th>
                        <th>{t('to')}</th>
                        <th>Antal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {insights.top_routes.map((r, i) => (
                        <tr key={i}>
                          <td>{r.from}</td>
                          <td>{r.to}</td>
                          <td className="count-col">{r.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-data">{t('noRoutes')}</p>
                )}
              </div>

              <div className="insights-panel">
                <h3 className="panel-title">{t('topGoods')}</h3>
                {insights.top_goods && insights.top_goods.length > 0 ? (
                  <table className="insights-table">
                    <thead>
                      <tr>
                        <th>Vare</th>
                        <th>Antal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {insights.top_goods.map((g, i) => (
                        <tr key={i}>
                          <td>{g.name}</td>
                          <td className="count-col">{g.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="no-data">{t('noGoods')}</p>
                )}
              </div>

              <div className="insights-panel">
                <h3 className="panel-title">{t('weeklyBreakdown')}</h3>
                {insights.weekly_breakdown && Object.keys(insights.weekly_breakdown).length > 0 ? (
                  <div className="weekly-bars">
                    {Object.entries(insights.weekly_breakdown).reverse().map(([week, count]) => {
                      const maxCount = Math.max(...Object.values(insights.weekly_breakdown), 1);
                      return (
                        <div key={week} className="weekly-bar-row">
                          <span className="weekly-label">{week}</span>
                          <div className="weekly-bar-bg">
                            <div
                              className="weekly-bar-fill"
                              style={{ width: `${(count / maxCount) * 100}%` }}
                            />
                          </div>
                          <span className="weekly-count">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="no-data">{t('noData')}</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Insights;
